import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export interface CronJob {
  jobid: number;
  schedule: string;
  command: string;
  nodename: string;
  nodeport: number;
  database: string;
  username: string;
  active: boolean;
  jobname: string;
}

export interface CronJobRun {
  runid: number;
  jobid: number;
  job_pid: number;
  database: string;
  username: string;
  command: string;
  status: string;
  return_message: string;
  start_time: string;
  end_time: string;
}

/**
 * Get all active cron jobs
 */
export async function getCronJobs(): Promise<CronJob[]> {
  try {
    const { data, error } = await supabase.rpc('exec_sql', {
      sql: `SELECT * FROM cron.job ORDER BY jobname;`
    });

    if (error) throw error;
    return (data as CronJob[]) || [];
  } catch (error) {
    logger.error('Failed to get cron jobs:', error);
    throw error;
  }
}

/**
 * Get recent cron job execution history
 */
export async function getCronJobHistory(limit: number = 50): Promise<CronJobRun[]> {
  try {
    const { data, error } = await supabase.rpc('exec_sql', {
      sql: `
        SELECT 
          runid,
          jobid,
          job_pid,
          database,
          username,
          command,
          status,
          return_message,
          start_time,
          end_time
        FROM cron.job_run_details 
        ORDER BY start_time DESC 
        LIMIT ${limit};
      `
    });

    if (error) throw error;
    return (data as CronJobRun[]) || [];
  } catch (error) {
    logger.error('Failed to get cron job history:', error);
    throw error;
  }
}

/**
 * Toggle cron job active status
 */
export async function toggleCronJob(jobname: string, active: boolean): Promise<void> {
  try {
    const { error } = await supabase.rpc('exec_sql', {
      sql: `UPDATE cron.job SET active = ${active} WHERE jobname = '${jobname}';`
    });

    if (error) throw error;
    logger.info(`Cron job ${jobname} ${active ? 'enabled' : 'disabled'}`);
  } catch (error) {
    logger.error(`Failed to toggle cron job ${jobname}:`, error);
    throw error;
  }
}

/**
 * Delete a cron job
 */
export async function deleteCronJob(jobname: string): Promise<void> {
  try {
    const { error } = await supabase.rpc('exec_sql', {
      sql: `SELECT cron.unschedule('${jobname}');`
    });

    if (error) throw error;
    logger.info(`Cron job ${jobname} deleted`);
  } catch (error) {
    logger.error(`Failed to delete cron job ${jobname}:`, error);
    throw error;
  }
}

/**
 * Disable all automation cron jobs (emergency stop)
 */
export async function disableAllCrons(): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke('disable-all-crons');
    
    if (error) throw error;
    logger.info('All cron jobs disabled');
  } catch (error) {
    logger.error('Failed to disable all crons:', error);
    throw error;
  }
}

/**
 * Setup automation cron jobs
 */
export async function setupAutomationCrons(): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke('setup-auto-resume-cron');
    
    if (error) throw error;
    logger.info('Automation cron jobs configured');
  } catch (error) {
    logger.error('Failed to setup automation crons:', error);
    throw error;
  }
}

/**
 * Pause problematic cron jobs automatically (backend)
 */
export async function pauseProblematicCrons(): Promise<void> {
  try {
    const { data, error } = await supabase.functions.invoke('pause-problematic-crons');
    
    if (error) throw error;
    logger.info('Problematic cron jobs paused:', data);
    return data;
  } catch (error) {
    logger.error('Failed to pause problematic crons:', error);
    throw error;
  }
}
